import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import QRCode from 'qrcode';
import { 
  Zap, 
  Send, 
  Receipt, 
  Users, 
  Clock, 
  Shield,
  ArrowLeft,
  Plus,
  Minus,
  User,
  Mail,
  Phone,

  Calendar,
  CheckCircle,
  AlertTriangle,
  Search,
  MoreHorizontal,
  Copy,
  Star,
  QrCode,
  Smartphone,
  Building,
  CreditCard,
  Eye,
  EyeOff,
  RefreshCw,
  X,
  Download
} from 'lucide-react';
import { accountsAPI, zelleAPI } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { handleApiError } from '../utils/errorHandler';
import { formatCurrency } from '../utils/currencyFormatter';

const Zelle = () => {
  const { user } = useAuth();
  
  // Currency formatter for user's currency
  const formatCurrencyWithUserCurrency = (amount) => {
    return formatCurrency(amount, user?.currency_code || 'USD');
  };
  
  const [activeTab, setActiveTab] = useState('send');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientContact, setRecipientContact] = useState('');
  const [recipientType, setRecipientType] = useState('email');
  const [memo, setMemo] = useState('');
  const [sendOption, setSendOption] = useState('now');
  const [splitAmount, setSplitAmount] = useState('');
  const [splitRecipients, setSplitRecipients] = useState([]);
  const [splitMemo, setSplitMemo] = useState('');
  
  // Data states
  const [accounts, setAccounts] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // QR Code states
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  
  const notification = useNotification();

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    console.log('showQRModal state changed to:', showQRModal);
  }, [showQRModal]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [accountsRes, recipientsRes, transactionsRes, limitsRes] = await Promise.all([
        accountsAPI.getAccounts(),
        zelleAPI.getRecipients(),
        zelleAPI.getTransactions({ limit: 20 }),
        zelleAPI.getLimits()
      ]);

      if (accountsRes.status === 'success') {
        setAccounts(accountsRes.data.accounts);
        if (accountsRes.data.accounts.length > 0) {
          setSelectedAccount(accountsRes.data.accounts[0].id);
        }
      }

      if (recipientsRes.status === 'success') {
        setRecipients(recipientsRes.data.recipients);
      }

      if (transactionsRes.status === 'success') {
        setTransactions(transactionsRes.data.transactions);
      }

      if (limitsRes.status === 'success') {
        setLimits(limitsRes.data.limits);
      }
    } catch (error) {
      handleApiError(error, notification, 'Failed to load Zelle data');
    } finally {
      setLoading(false);
    }
  };



  const handleSendMoney = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const sendData = {
        bank_account_id: selectedAccount,
        amount: parseFloat(amount),
        recipient_name: recipientName,
        memo: memo || undefined
      };

      if (recipientType === 'email') {
        sendData.recipient_email = recipientContact;
      } else {
        sendData.recipient_phone = recipientContact;
      }

      const response = await zelleAPI.send(sendData);
      
      if (response.status === 'success') {
        notification.showSuccess('Money sent successfully!');
        setAmount('');
        setRecipientName('');
        setRecipientContact('');
        setMemo('');
        fetchInitialData(); // Refresh data
      }
    } catch (error) {
      const errorData = handleApiError(error, notification);
      setErrors(errorData.errors || {});
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMoney = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const requestData = {
        bank_account_id: selectedAccount,
        amount: parseFloat(amount),
        recipient_name: recipientName,
        memo: memo || undefined
      };

      if (recipientType === 'email') {
        requestData.recipient_email = recipientContact;
      } else {
        requestData.recipient_phone = recipientContact;
      }

      const response = await zelleAPI.request(requestData);
      
      if (response.status === 'success') {
        notification.showSuccess('Money request sent successfully!');
        setAmount('');
        setRecipientName('');
        setRecipientContact('');
        setMemo('');
        fetchInitialData(); // Refresh data
      }
    } catch (error) {
      const errorData = handleApiError(error, notification);
      setErrors(errorData.errors || {});
    } finally {
      setLoading(false);
    }
  };

  const handleSplitPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    // Validate split payment data
    if (!selectedAccount) {
      notification.showError('Please select an account');
      setLoading(false);
      return;
    }

    if (!splitAmount || parseFloat(splitAmount) <= 0) {
      notification.showError('Please enter a valid amount to split');
      setLoading(false);
      return;
    }

    if (splitRecipients.length === 0) {
      notification.showError('Please add at least one person to split with');
      setLoading(false);
      return;
    }

    // Validate all recipients have name and contact
    const invalidRecipients = splitRecipients.some(recipient => 
      !recipient.name.trim() || !recipient.contact.trim()
    );

    if (invalidRecipients) {
      notification.showError('Please fill in name and contact for all recipients');
      setLoading(false);
      return;
    }

    try {
      const splitAmountValue = parseFloat(splitAmount);
      const individualAmount = splitAmountValue / (splitRecipients.length + 1); // +1 for yourself

      // Send individual requests to each recipient
      const requestPromises = splitRecipients.map(async (recipient) => {
        const requestData = {
          bank_account_id: selectedAccount,
          amount: individualAmount,
          recipient_name: recipient.name,
          memo: splitMemo || `Split payment - ${formatCurrencyWithUserCurrency(splitAmountValue)} total`
        };

        // Determine if contact is email or phone
        if (recipient.contact.includes('@')) {
          requestData.recipient_email = recipient.contact;
        } else {
          requestData.recipient_phone = recipient.contact;
        }

        return zelleAPI.request(requestData);
      });

      const responses = await Promise.all(requestPromises);
      
      // Check if all requests were successful
      const allSuccessful = responses.every(response => response.status === 'success');
      
      if (allSuccessful) {
        notification.showSuccess(`Split payment requests sent successfully! Each person will pay ${formatCurrencyWithUserCurrency(individualAmount)}`);
        
        // Reset form
        setSplitAmount('');
        setSplitRecipients([]);
        setSplitMemo('');
        fetchInitialData(); // Refresh data
      } else {
        notification.showError('Some requests failed. Please try again.');
      }
    } catch (error) {
      const errorData = handleApiError(error, notification, 'Failed to send split payment requests');
      setErrors(errorData.errors || {});
    } finally {
      setLoading(false);
    }
  };

  const selectRecipient = (recipient) => {
    setRecipientName(recipient.name);
    if (recipient.email) {
      setRecipientContact(recipient.email);
      setRecipientType('email');
    } else if (recipient.phone) {
      setRecipientContact(recipient.phone);
      setRecipientType('phone');
    }
  };

  const addSplitRecipient = () => {
    setSplitRecipients([...splitRecipients, { name: '', contact: '', amount: '' }]);
  };

  const removeSplitRecipient = (index) => {
    setSplitRecipients(splitRecipients.filter((_, i) => i !== index));
  };

  const updateSplitRecipient = (index, field, value) => {
    const updated = splitRecipients.map((recipient, i) => 
      i === index ? { ...recipient, [field]: value } : recipient
    );
    setSplitRecipients(updated);
  };

  const calculateSplitAmount = () => {
    if (!splitAmount || splitRecipients.length === 0) return 0;
    return parseFloat(splitAmount) / (splitRecipients.length + 1);
  };

  const generateQRCode = async () => {
    console.log('generateQRCode called, setting loading to true');
    setQrCodeLoading(true);
    
    try {
      // Use the user's actual QR data if available, otherwise use a default Zelle URL
      let qrData;
      
      if (user?.zelleQrData) {
        // User has custom QR data set by admin
        qrData = user.zelleQrData;
      } else {
        // Fallback to default Zelle enrollment URL with user's email
        const encodedData = btoa(JSON.stringify({
          name: user?.name || 'User',
          action: 'payment',
          token: user?.email || 'user@example.com'
        }));
        qrData = `https://enroll.zellepay.com/qr-codes?data=${encodedData}`;
      }
      
      console.log('Generating QR code with data:', qrData);
      
      // Generate QR code as data URL
      const qrCodeDataURL = await QRCode.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#7c3aed', // Purple color
          light: '#ffffff'
        }
      });
      
      console.log('QR code generated successfully, setting data and showing modal');
      setQrCodeData(qrCodeDataURL);
      setShowQRModal(true);
      console.log('Modal state should now be true');
      
    } catch (error) {
      console.error('QR Code generation error:', error);
      notification.showError('Failed to generate QR code');
    } finally {
      console.log('Setting loading to false');
      setQrCodeLoading(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeData) return;
    
    const link = document.createElement('a');
    link.download = 'zelle-qr-code.png';
    link.href = qrCodeData;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyQRCode = async () => {
    try {
      // Convert data URL to blob
      const response = await fetch(qrCodeData);
      const blob = await response.blob();
      
      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      
      notification.showSuccess('QR code copied to clipboard!');
    } catch (error) {
      notification.showError('Failed to copy QR code');
    }
  };

  const closeModal = () => {
    console.log('closeModal called');
    setShowQRModal(false);
    setQrCodeData('');
  };

  if (loading && accounts.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading Zelle...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft className="w-6 h-6 text-gray-600" />
              </Link>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Zelle®</h1>
                  <p className="text-sm text-gray-500">Send money in minutes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-0">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {[
                { id: 'send', label: 'Send Money', icon: Send },
                { id: 'request', label: 'Request Money', icon: Receipt },
                { id: 'split', label: 'Split Payment', icon: Users },
                { id: 'activity', label: 'Activity', icon: Clock }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className={`-ml-0.5 mr-2 h-5 w-5 ${
                    activeTab === tab.id ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 min-h-0">
          {/* Main Content */}
          <div className="xl:col-span-2 min-h-0">
            {activeTab === 'send' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl shadow-sm p-8"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Money</h2>
                
                <form onSubmit={handleSendMoney} className="space-y-6">
                  {/* Account Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      From Account
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {accounts.map((account) => (
                        <div
                          key={account.id}
                          onClick={() => setSelectedAccount(account.id)}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedAccount === account.id
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              account.account_type === 'checking' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                            }`}>
                              <CreditCard className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{account.account_name}</p>
                              <p className="text-sm text-gray-500">{account.formatted_account_number}</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {formatCurrencyWithUserCurrency(account.balance)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recipient Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Recipient Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder="Enter recipient's full name"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <User className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    {errors.recipient_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.recipient_name[0]}</p>
                    )}
                  </div>

                  {/* Recipient Contact */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Send To
                    </label>
                    <div className="flex space-x-2 mb-4">
                      <button
                        type="button"
                        onClick={() => setRecipientType('email')}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                          recipientType === 'email'
                            ? 'bg-purple-100 text-purple-700 border border-purple-300'
                            : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        <Mail className="w-4 h-4 inline mr-2" />
                        Email
                      </button>
                      <button
                        type="button"
                        onClick={() => setRecipientType('phone')}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                          recipientType === 'phone'
                            ? 'bg-purple-100 text-purple-700 border border-purple-300'
                            : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        <Phone className="w-4 h-4 inline mr-2" />
                        Phone
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={recipientType === 'email' ? 'email' : 'tel'}
                        value={recipientContact}
                        onChange={(e) => setRecipientContact(e.target.value)}
                        placeholder={recipientType === 'email' ? 'Enter email address' : 'Enter phone number'}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        {recipientType === 'email' ? (
                          <Mail className="w-5 h-5 text-gray-400" />
                        ) : (
                          <Phone className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    {errors.recipient_email && (
                      <p className="text-red-500 text-sm mt-1">{errors.recipient_email[0]}</p>
                    )}
                    {errors.recipient_phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.recipient_phone[0]}</p>
                    )}
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Amount
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0.01"
                        max="5000"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg font-semibold"
                        required
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <span className="text-gray-400 font-medium text-lg">
                          {user?.currency_code === 'USD' ? '$' : 
                           user?.currency_code === 'EUR' ? '€' : 
                           user?.currency_code === 'GBP' ? '£' : 
                           user?.currency_code === 'NGN' ? '₦' : 
                           user?.currency_code === 'GHS' ? '₵' : 
                           user?.currency_code === 'KES' ? 'KSh' : 
                           user?.currency_code === 'ZAR' ? 'R' : 
                           user?.currency_code === 'CAD' ? 'C$' : 
                           user?.currency_code === 'AUD' ? 'A$' : 
                           user?.currency_code === 'JPY' ? '¥' : 
                           user?.currency_code === 'CHF' ? 'CHF' : 
                           user?.currency_code === 'CNY' ? '¥' : 
                           user?.currency_code === 'INR' ? '₹' : 
                           user?.currency_code === 'BRL' ? 'R$' : 
                           user?.currency_code === 'MXN' ? '$' : 
                           user?.currency_code || '$'}
                        </span>
                      </div>
                    </div>
                    {limits && (
                      <p className="text-sm text-gray-500 mt-2">
                        Daily limit: {formatCurrencyWithUserCurrency(limits.daily.limit)} • Used: {formatCurrencyWithUserCurrency(limits.daily.used)}
                      </p>
                    )}
                    {errors.amount && (
                      <p className="text-red-500 text-sm mt-1">{errors.amount[0]}</p>
                    )}
                  </div>

                  {/* Memo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      What's this for? (Optional)
                    </label>
                    <input
                      type="text"
                      value={memo}
                      onChange={(e) => setMemo(e.target.value)}
                      placeholder="Add a note"
                      maxLength="255"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {/* Send Button */}
                  <button
                    type="submit"
                    disabled={loading || !selectedAccount || !recipientName || !recipientContact || !amount}
                    className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send {amount && formatCurrencyWithUserCurrency(parseFloat(amount) || 0)}</span>
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            )}

            {activeTab === 'request' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl shadow-sm p-8"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Request Money</h2>
                
                <form onSubmit={handleRequestMoney} className="space-y-6">
                  {/* Account Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Receive To
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {accounts.map((account) => (
                        <div
                          key={account.id}
                          onClick={() => setSelectedAccount(account.id)}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedAccount === account.id
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              account.account_type === 'checking' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                            }`}>
                              <CreditCard className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{account.account_name}</p>
                              <p className="text-sm text-gray-500">{account.formatted_account_number}</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {formatCurrencyWithUserCurrency(account.balance)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recipient Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Request From
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder="Enter person's full name"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <User className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    {errors.recipient_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.recipient_name[0]}</p>
                    )}
                  </div>

                  {/* Recipient Contact */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Contact Information
                    </label>
                    <div className="flex space-x-2 mb-4">
                      <button
                        type="button"
                        onClick={() => setRecipientType('email')}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                          recipientType === 'email'
                            ? 'bg-purple-100 text-purple-700 border border-purple-300'
                            : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        <Mail className="w-4 h-4 inline mr-2" />
                        Email
                      </button>
                      <button
                        type="button"
                        onClick={() => setRecipientType('phone')}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                          recipientType === 'phone'
                            ? 'bg-purple-100 text-purple-700 border border-purple-300'
                            : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        <Phone className="w-4 h-4 inline mr-2" />
                        Phone
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={recipientType === 'email' ? 'email' : 'tel'}
                        value={recipientContact}
                        onChange={(e) => setRecipientContact(e.target.value)}
                        placeholder={recipientType === 'email' ? 'Enter email address' : 'Enter phone number'}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        {recipientType === 'email' ? (
                          <Mail className="w-5 h-5 text-gray-400" />
                        ) : (
                          <Phone className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    {errors.recipient_email && (
                      <p className="text-red-500 text-sm mt-1">{errors.recipient_email[0]}</p>
                    )}
                    {errors.recipient_phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.recipient_phone[0]}</p>
                    )}
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Amount
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0.01"
                        max="5000"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg font-semibold"
                        required
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <span className="text-gray-400 font-medium text-lg">
                          {user?.currency_code === 'USD' ? '$' : 
                           user?.currency_code === 'EUR' ? '€' : 
                           user?.currency_code === 'GBP' ? '£' : 
                           user?.currency_code === 'NGN' ? '₦' : 
                           user?.currency_code === 'GHS' ? '₵' : 
                           user?.currency_code === 'KES' ? 'KSh' : 
                           user?.currency_code === 'ZAR' ? 'R' : 
                           user?.currency_code === 'CAD' ? 'C$' : 
                           user?.currency_code === 'AUD' ? 'A$' : 
                           user?.currency_code === 'JPY' ? '¥' : 
                           user?.currency_code === 'CHF' ? 'CHF' : 
                           user?.currency_code === 'CNY' ? '¥' : 
                           user?.currency_code === 'INR' ? '₹' : 
                           user?.currency_code === 'BRL' ? 'R$' : 
                           user?.currency_code === 'MXN' ? '$' : 
                           user?.currency_code || '$'}
                        </span>
                      </div>
                    </div>
                    {errors.amount && (
                      <p className="text-red-500 text-sm mt-1">{errors.amount[0]}</p>
                    )}
                  </div>

                  {/* Memo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      What's this for? (Optional)
                    </label>
                    <input
                      type="text"
                      value={memo}
                      onChange={(e) => setMemo(e.target.value)}
                      placeholder="Add a note about what this request is for"
                      maxLength="255"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {/* Request Button */}
                  <button
                    type="submit"
                    disabled={loading || !selectedAccount || !recipientName || !recipientContact || !amount}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <Receipt className="w-5 h-5" />
                        <span>Request {amount && formatCurrencyWithUserCurrency(parseFloat(amount) || 0)}</span>
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            )}

            {activeTab === 'split' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl shadow-sm p-8 max-h-[80vh] overflow-y-auto"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Split Payment</h2>
                
                <form onSubmit={handleSplitPayment} className="space-y-6">
                  {/* Account Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      From Account
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {accounts.map((account) => (
                        <div
                          key={account.id}
                          onClick={() => setSelectedAccount(account.id)}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedAccount === account.id
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              account.account_type === 'checking' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                            }`}>
                              <CreditCard className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{account.account_name}</p>
                              <p className="text-sm text-gray-500">{account.formatted_account_number}</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {formatCurrencyWithUserCurrency(account.balance)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Total Amount to Split
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={splitAmount}
                        onChange={(e) => setSplitAmount(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0.01"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg font-semibold"
                        required
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <span className="text-gray-400 font-medium text-lg">
                          {user?.currency_code === 'USD' ? '$' : 
                           user?.currency_code === 'EUR' ? '€' : 
                           user?.currency_code === 'GBP' ? '£' : 
                           user?.currency_code === 'NGN' ? '₦' : 
                           user?.currency_code === 'GHS' ? '₵' : 
                           user?.currency_code === 'KES' ? 'KSh' : 
                           user?.currency_code === 'ZAR' ? 'R' : 
                           user?.currency_code === 'CAD' ? 'C$' : 
                           user?.currency_code === 'AUD' ? 'A$' : 
                           user?.currency_code === 'JPY' ? '¥' : 
                           user?.currency_code === 'CHF' ? 'CHF' : 
                           user?.currency_code === 'CNY' ? '¥' : 
                           user?.currency_code === 'INR' ? '₹' : 
                           user?.currency_code === 'BRL' ? 'R$' : 
                           user?.currency_code === 'MXN' ? '$' : 
                           user?.currency_code || '$'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Split With
                      </label>
                      <button
                        type="button"
                        onClick={addSplitRecipient}
                        className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1 px-3 py-1 rounded-lg hover:bg-purple-50 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Person</span>
                      </button>
                    </div>
                    
                    <div className={`space-y-3 max-h-64 overflow-y-auto ${splitRecipients.length > 3 ? 'border-t border-b border-gray-100 py-2' : ''}`}>
                      {splitRecipients.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">Click "Add Person" to split the payment</p>
                        </div>
                      ) : (
                        splitRecipients.map((recipient, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            {/* Desktop Layout */}
                            <div className="hidden sm:flex items-center space-x-3">
                              <input
                                type="text"
                                placeholder="Name"
                                value={recipient.name}
                                onChange={(e) => updateSplitRecipient(index, 'name', e.target.value)}
                                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                              <input
                                type="text"
                                placeholder="Email or Phone"
                                value={recipient.contact}
                                onChange={(e) => updateSplitRecipient(index, 'contact', e.target.value)}
                                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                              <div className="text-sm font-semibold text-gray-900 min-w-[80px] text-right">
                                {splitAmount && formatCurrencyWithUserCurrency(calculateSplitAmount())}
                              </div>
                              <button
                                type="button"
                                onClick={() => removeSplitRecipient(index)}
                                className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                            </div>
                            
                            {/* Mobile Layout */}
                            <div className="sm:hidden space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">Person {index + 1}</span>
                                <button
                                  type="button"
                                  onClick={() => removeSplitRecipient(index)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                              </div>
                              <input
                                type="text"
                                placeholder="Name"
                                value={recipient.name}
                                onChange={(e) => updateSplitRecipient(index, 'name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                              <input
                                type="text"
                                placeholder="Email or Phone"
                                value={recipient.contact}
                                onChange={(e) => updateSplitRecipient(index, 'contact', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Amount:</span>
                                <span className="text-sm font-semibold text-gray-900">
                                  {splitAmount && formatCurrencyWithUserCurrency(calculateSplitAmount())}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {splitRecipients.length > 3 && (
                      <div className="text-xs text-gray-500 text-center mt-2">
                        Scroll to see more recipients
                      </div>
                    )}
                    
                    {splitRecipients.length > 0 && (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={addSplitRecipient}
                          className="w-full text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center justify-center space-x-1 py-2 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Another Person</span>
                        </button>
                      </div>
                    )}

                    {splitAmount && splitRecipients.length > 0 && (
                      <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Your share:</span>
                          <span className="font-semibold text-purple-700">
                            {formatCurrencyWithUserCurrency(calculateSplitAmount())}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-sm text-gray-600">Each person pays:</span>
                          <span className="font-semibold text-purple-700">
                            {formatCurrencyWithUserCurrency(calculateSplitAmount())}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      What's this for?
                    </label>
                    <input
                      type="text"
                      value={splitMemo}
                      onChange={(e) => setSplitMemo(e.target.value)}
                      placeholder="Dinner, groceries, trip, etc."
                      maxLength="255"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !selectedAccount || !splitAmount || splitRecipients.length === 0}
                    className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <Users className="w-5 h-5" />
                        <span>Send Split Requests</span>
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            )}

            {activeTab === 'activity' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl shadow-sm"
              >
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Zelle Activity</h2>
                    <button 
                      onClick={fetchInitialData}
                      disabled={loading}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>
                
                {transactions.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Zap className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Zelle Activity Yet</h3>
                    <p className="text-gray-500">Your Zelle transactions will appear here</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {transactions.map((transaction, index) => (
                      <motion.div
                        key={transaction.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.05 }}
                        className="p-6 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            transaction.type === 'sent' ? 'bg-red-100 text-red-600' :
                            transaction.type === 'received' ? 'bg-green-100 text-green-600' :
                            'bg-yellow-100 text-yellow-600'
                          }`}>
                            {transaction.type === 'sent' ? <Send className="w-6 h-6" /> :
                             transaction.type === 'received' ? <Receipt className="w-6 h-6" /> :
                             <Clock className="w-6 h-6" />}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900">
                                {transaction.type === 'sent' && `To ${transaction.recipient_name}`}
                                {transaction.type === 'received' && `From ${transaction.sender_name}`}
                                {transaction.type === 'request' && `Request to ${transaction.recipient_name}`}
                              </p>
                              <div className="text-right">
                                <p className={`text-sm font-semibold ${
                                  transaction.type === 'received' ? 'text-green-600' : 'text-gray-900'
                                }`}>
                                  {transaction.type === 'received' ? '+' : transaction.type === 'request' ? '' : '-'}
                                  {formatCurrencyWithUserCurrency(transaction.amount)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-xs text-gray-500">{transaction.memo || 'No memo'}</p>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">
                                  {transaction.formatted_date}
                                </span>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  transaction.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {transaction.status}
                                </span>
                              </div>
                            </div>
                            {transaction.reference_number && (
                              <p className="text-xs text-gray-400 mt-1">Ref: {transaction.reference_number}</p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Recent Recipients */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-2xl shadow-sm"
            >
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Recent Recipients</h3>
              </div>
              <div className="p-6 space-y-4">
                {recipients.length === 0 ? (
                  <div className="text-center py-4">
                    <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No recent recipients</p>
                  </div>
                ) : (
                  recipients.slice(0, 4).map((recipient, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between group cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-2 rounded-lg transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-semibold text-sm">
                          {recipient.avatar}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 flex items-center space-x-1">
                            <span>{recipient.name}</span>
                            {recipient.transaction_count > 5 && <Star className="w-3 h-3 text-yellow-500 fill-current" />}
                          </p>
                          <p className="text-xs text-gray-500">{recipient.email || recipient.phone}</p>
                        </div>
                      </div>
                      <button 
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => {
                          selectRecipient(recipient);
                          setActiveTab('send');
                        }}
                      >
                        <Send className="w-4 h-4 text-purple-600" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </motion.div>

            {/* Security Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm"
            >
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Shield className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-bold text-gray-900">Security & Limits</h3>
                </div>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Daily send limit:</span>
                    <span className="font-semibold">{formatCurrencyWithUserCurrency(2500)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Monthly send limit:</span>
                    <span className="font-semibold">{formatCurrencyWithUserCurrency(20000)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Daily receive limit:</span>
                    <span className="font-semibold">{formatCurrencyWithUserCurrency(5000)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <p className="text-xs text-gray-500">
                      Zelle® and the Zelle® related marks are wholly owned by Early Warning Services, LLC and are used herein under license.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm"
            >
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('QR Code button clicked');
                      generateQRCode();
                    }}
                    disabled={qrCodeLoading}
                    className="w-full flex items-center space-x-3 text-left p-3 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {qrCodeLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-600 border-t-transparent" />
                    ) : (
                      <QrCode className="w-5 h-5 text-purple-600" />
                    )}
                    <span className="text-sm font-medium text-gray-900">
                      {qrCodeLoading ? 'Generating...' : 'Show My QR Code'}
                    </span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full text-center sm:text-left">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        My Zelle QR Code
                      </h3>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Close button clicked');
                          closeModal();
                        }}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-6">
                        Share this QR code with others so they can easily send you money via Zelle®
                      </p>
                      
                      <div className="flex justify-center mb-6">
                        <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                          {qrCodeData ? (
                            <img 
                              src={qrCodeData} 
                              alt="Zelle QR Code" 
                              className="w-64 h-64"
                            />
                          ) : (
                            <div className="w-64 h-64 flex items-center justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent"></div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={copyQRCode}
                          disabled={!qrCodeData}
                          className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                        >
                          <Copy className="w-4 h-4" />
                          <span>Copy QR Code</span>
                        </button>
                        
                        <button
                          onClick={downloadQRCode}
                          disabled={!qrCodeData}
                          className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </button>
                      </div>
                      
                      <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                        <p className="text-xs text-purple-700">
                          <strong>Note:</strong> This QR code contains your contact information for Zelle® payments. Only share it with people you trust.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Zelle; 